// Logo picker: upload a PNG/JPEG/WebP (≤ 1 MB) for a widget, see it, or remove
// it. The file goes straight to S3 (presigned by the console API) and the
// resulting https:// URL is handed to onChange; the parent saves it.
import { useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { useApi } from "../api";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 1024 * 1024;

export function LogoUpload({ widgetId, value, onChange, hint }: { widgetId: string; value: string | null; onChange: (url: string | null) => void; hint?: string }) {
  const api = useApi();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setError(null);
    // Checked here for a quick message; the upload link enforces the same limits
    if (!TYPES.includes(file.type)) return setError("Please choose a PNG, JPEG or WebP image.");
    if (file.size > MAX_BYTES) return setError("That image is over 1 MB. Please use a smaller one.");
    setBusy(true);
    try {
      onChange(await api.uploadLogo(widgetId, file));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <div className="logo-upload">
      <div className="logo-upload-row">
        <div className="logo-upload-thumb">{value ? <img src={value} alt="Logo" /> : <span className="muted">No logo</span>}</div>
        <div className="logo-upload-actions">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => input.current?.click()}>
            <ImagePlus size={16} aria-hidden="true" /> {busy ? "Uploading…" : value ? "Replace" : "Upload logo"}
          </button>
          {value && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => onChange(null)}>
              <Trash2 size={16} aria-hidden="true" /> Remove
            </button>
          )}
        </div>
      </div>
      <input ref={input} type="file" accept={TYPES.join(",")} hidden onChange={(e) => void pick(e.target.files?.[0])} />
      {hint && <p className="hosted-hint">{hint}</p>}
      {error && <p className="price-error">{error}</p>}
    </div>
  );
}
